// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import AppThemeProvider from './theme/AppThemeProvider'
import App from './App'
import '@fontsource-variable/inter'
import './index.css'
import { resolvePharmacyFromUrl } from './auth/pharmacyUrl'

// RX-PERF-10: TanStack Query is the sanctioned home for polling + caching going forward —
// the hand-rolled fetch/poll/cache in every view is exactly how the /rx request storms
// happened (818 KB badge fetches, batch refires, per-tab config refetches). New data flows
// use queries; existing surfaces migrate opportunistically. Defaults encode the house rules:
// nothing refetches on remount inside 30s, focus refetch is on (matches the tab-refocus
// reload convention), and intervals are opt-in per query — never ambient.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
})

// For a path-based pharmacy URL (/p/{slug}/...), mount the router under that prefix so EVERY
// link/navigation keeps the slug. Subdomain pharmacies carry context via the host (no basename),
// and apex/admin hosts get no basename. basename is read once from the URL at load; full-page
// SSO redirects re-evaluate it on return.
const { source: pharmacySource, slug: pharmacySlug } = resolvePharmacyFromUrl()
const routerBasename = pharmacySource === 'path' && pharmacySlug ? `/p/${pharmacySlug}` : undefined

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* D-UI-3: the shared kit's theme (components/ui). Changes no existing screen. */}
      <AppThemeProvider>
        <BrowserRouter basename={routerBasename}>
          <App />
        </BrowserRouter>
      </AppThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
